import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Input, Select, Button, Upload, message, Space } from 'antd';
import { ArrowRightOutlined, UploadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { faqsApi, show422Error, normalizeFaqApiBody } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { FormSkeleton } from '../../components/SkeletonLoaders';

const { Option } = Select;

const normFiles = (e) => {
  if (Array.isArray(e)) return e;
  return e?.fileList || [];
};

const SuperadminFaqCreate = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const res = await faqsApi.getCreateMeta();
        const root = normalizeFaqApiBody(res);
        if (cancelled) return;
        setCategories(root.categories || []);
      } catch (error) {
        if (!cancelled) message.error(error?.message || 'خطا در دریافت اطلاعات اولیه');
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onFinish = async (values) => {
    const fd = new FormData();
    fd.append('title', values.title?.trim() || '');
    fd.append('description', values.description || '');
    fd.append('category_id', String(values.category_id ?? ''));
    const files = values.files || [];
    files.forEach((f) => {
      const file = f?.originFileObj || f;
      if (file && file instanceof Blob) fd.append('file[]', file);
    });

    setSubmitting(true);
    try {
      await faqsApi.create(fd);
      message.success('سوال ایجاد شد');
      navigate('/settings/superadmin-faqs');
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ایجاد سوال');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-900/25">
                <QuestionCircleOutlined className="text-xl text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl">ایجاد سوال جدید</h1>
                <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">سوال و پاسخ/توضیحات را ثبت کنید</p>
              </div>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/settings/superadmin-faqs')}>
              بازگشت به لیست
            </Button>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {loadingMeta ? (
                <FormSkeleton fields={6} />
              ) : (
                <Form form={form} layout="vertical" onFinish={onFinish}>
                  <Form.Item name="title" label="عنوان" rules={[{ required: true, message: 'عنوان را وارد کنید' }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="category_id" label="دسته‌بندی" rules={[{ required: true, message: 'دسته‌بندی را انتخاب کنید' }]}>
                    <Select popupClassName="custom-font" showSearch optionFilterProp="children" placeholder="انتخاب دسته‌بندی">
                      {categories.map((c) => (
                        <Option key={c.id} value={c.id}>
                          {c.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="description" label="توضیحات" rules={[{ required: true, message: 'توضیحات را وارد کنید' }]}>
                    <Input.TextArea rows={6} placeholder="توضیحات را بنویسید" />
                  </Form.Item>

                  <Form.Item name="files" label="فایل‌ها" valuePropName="fileList" getValueFromEvent={normFiles}>
                    <Upload multiple beforeUpload={() => false}>
                      <Button icon={<UploadOutlined />}>انتخاب فایل‌ها</Button>
                    </Upload>
                  </Form.Item>

                  <Form.Item className="mb-0">
                    <Space wrap>
                      <Button type="primary" htmlType="submit" loading={submitting} className={PANEL_PRIMARY_BTN_CLASS}>
                        ثبت
                      </Button>
                      <Button onClick={() => navigate('/settings/superadmin-faqs')}>انصراف</Button>
                    </Space>
                  </Form.Item>
                </Form>
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SuperadminFaqCreate;
